import { useState, useRef, useCallback, useEffect } from 'react';
import { authApi } from '@/lib/auth';
import { useAuthStore } from '@/stores/auth';
import { useLogout } from '@/hooks/useAuth';
import {
  TERMS_OF_SERVICE,
  TERMS_EFFECTIVE_DATE,
  TERMS_COMPANY_INFO,
  TERMS_CLOSING_STATEMENT,
  CURRENT_TERMS_VERSION,
  type TermsSection,
} from '@/lib/terms-content';

interface TermsOfServiceOverlayProps {
  onAccepted: () => void;
}

// SAFETY: dangerouslySetInnerHTML is used below because ToS content in
// terms-content.ts contains <strong> tags for emphasis. This is safe because
// the content is compile-time static (hardcoded in the bundle), never from
// user input or an API response. If content ever becomes dynamic, switch to
// a sanitization library (e.g., DOMPurify) or a markdown renderer.
function renderSection(section: TermsSection) {
  return (
    <div key={section.number} className="mb-4">
      <h3 className="font-semibold text-gray-900 mb-1">
        {section.number}. {section.title}
      </h3>
      {section.content && (
        <div
          className="text-gray-700 whitespace-pre-line"
          dangerouslySetInnerHTML={{ __html: section.content }}
        />
      )}
      {section.subsections?.map((sub) => (
        <div key={sub.number} className="ml-4 mt-3">
          <h4 className="font-medium text-gray-800 mb-1">
            {sub.number} {sub.title}
          </h4>
          <div
            className="text-gray-700 whitespace-pre-line"
            dangerouslySetInnerHTML={{ __html: sub.content }}
          />
        </div>
      ))}
    </div>
  );
}

export function TermsOfServiceOverlay({ onAccepted }: TermsOfServiceOverlayProps) {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { updateUser } = useAuthStore();
  const { mutate: doLogout, isPending: isLoggingOut } = useLogout();

  // If the ToS content fits entirely within the scroll container (e.g., very
  // tall viewport), the scroll event will never fire. Detect this on mount
  // and auto-enable the checkbox so users aren't permanently locked out.
  useEffect(() => {
    const el = scrollRef.current;
    if (el && el.scrollHeight <= el.clientHeight + 20) {
      setHasScrolledToBottom(true);
    }
  }, []);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) {
      setHasScrolledToBottom(true);
    }
  }, []);

  const handleAccept = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      const response = await authApi.acceptTerms();
      if (response.success) {
        updateUser({ termsAcceptedVersion: CURRENT_TERMS_VERSION });
        onAccepted();
      } else {
        setError(response.error || 'Failed to accept terms. Please try again.');
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] bg-white flex items-center justify-center p-4">
      <div className="w-full max-w-2xl flex flex-col" style={{ height: '90vh', maxHeight: '90vh' }}>
        {/* Header */}
        <div className="text-center mb-6 flex-shrink-0">
          <div className="inline-block h-1 w-16 bg-gradient-to-r from-[#17DDAB] to-[#009FE4] rounded-full mb-4" />
          <h1 className="text-2xl font-semibold text-gray-900">Terms of Service</h1>
          <p className="mt-1 text-sm text-gray-500">
            Please review and accept to continue using Transakt
          </p>
        </div>

        {/* Scrollable ToS body */}
        <div className="relative flex-1 min-h-0 mb-4 overflow-hidden">
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="h-full overflow-y-auto border border-gray-200 rounded-lg p-6 text-sm leading-relaxed bg-gray-50"
          >
            {/* ToS Header */}
            <div className="text-center mb-6">
              <p className="font-bold text-gray-900 uppercase">{TERMS_COMPANY_INFO.name}</p>
              <p className="font-bold text-gray-900 uppercase">Terms of Service</p>
              <p className="text-gray-600 mt-1">Effective Date: {TERMS_EFFECTIVE_DATE}</p>
            </div>

            {/* ToS Sections */}
            {TERMS_OF_SERVICE.map(renderSection)}

            {/* Closing Statement */}
            <div className="mt-6 pt-4 border-t border-gray-300">
              <p className="font-semibold text-gray-900 text-xs leading-relaxed">
                {TERMS_CLOSING_STATEMENT}
              </p>
            </div>
          </div>

          {/* Scroll indicator */}
          {!hasScrolledToBottom && (
            <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-gray-50 to-transparent pointer-events-none flex items-end justify-center pb-1 rounded-b-lg">
              <span className="text-xs text-gray-400 animate-bounce">
                ↓ Scroll to read all terms
              </span>
            </div>
          )}
        </div>

        {/* Checkbox + Button */}
        <div className="flex-shrink-0 space-y-3">
          <label className="flex items-start gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              disabled={!hasScrolledToBottom || isSubmitting}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#009FE4] focus:ring-[#009FE4] disabled:opacity-50"
            />
            <span
              className={`text-sm ${hasScrolledToBottom ? 'text-gray-700' : 'text-gray-400'}`}
            >
              I have read and agree to the Terms of Service
            </span>
          </label>

          {error && (
            <div className="rounded-md bg-red-50 p-3 border border-red-200">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <button
            type="button"
            onClick={handleAccept}
            disabled={!termsAccepted || isSubmitting}
            className="w-full flex justify-center items-center py-2.5 px-4 rounded-md shadow-sm text-sm font-medium text-white bg-gradient-to-r from-[#17DDAB] to-[#009FE4] hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009FE4] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Accepting...
              </>
            ) : (
              'Accept & Continue'
            )}
          </button>

          <button
            type="button"
            onClick={() => doLogout()}
            disabled={isSubmitting || isLoggingOut}
            className="w-full text-center text-sm text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            I do not agree &mdash; log me out
          </button>
        </div>
      </div>
    </div>
  );
}
